import { Checkbox } from "@/v2/components/ui/checkbox";
import { Skeleton } from "@/v2/components/ui/skeleton";
import { useSeasons } from "../api";
import type { Season } from "../types";

type SeasonColumnProps = {
  selectedSeason: Season[];
  onSelectionChange: (next: Season[]) => void;
};

export function SeasonColumn({ selectedSeason, onSelectionChange }: SeasonColumnProps) {
  const { data: seasons, isLoading } = useSeasons();

  function toggle(season: Season, checked: boolean) {
    if (checked) {
      onSelectionChange([...selectedSeason, season]);
    } else {
      onSelectionChange(selectedSeason.filter((s) => s.season_id !== season.season_id));
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-card flex flex-col min-h-0">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="font-bold text-slate-900 text-base">1 · Season</div>
        <span className="text-sm font-bold px-3 py-1 rounded-full bg-action-light text-action">
          {selectedSeason.length} selected
        </span>
      </div>
      <div className="p-2 overflow-y-auto max-h-80">
        {isLoading ? (
          <div data-testid="season-column-skeleton" className="flex flex-col gap-2 p-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : !seasons || seasons.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">No data found</div>
        ) : (
          seasons.map((season, i) => {
            const checked = selectedSeason.some((s) => s.season_id === season.season_id);
            return (
              <label
                key={season.season_id}
                className={`flex items-center justify-between gap-4 px-3 py-3 rounded-xl cursor-pointer ${
                  checked ? "bg-action-light" : "hover:bg-accent"
                } ${i > 0 ? "border-t border-slate-100" : ""}`}
              >
                <div className="flex items-center gap-4">
                  <Checkbox
                    aria-label={season.season_name}
                    checked={checked}
                    onCheckedChange={(next) => toggle(season, next === true)}
                    className="size-5 rounded-md shrink-0"
                  />
                  <span className="text-base font-semibold text-slate-800 truncate" title={season.season_name}>
                    {season.season_name}
                  </span>
                </div>
                {checked && <span className="text-sm font-semibold text-slate-500 shrink-0">Current</span>}
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}
